import {
  Box,
  Button,
  Divider,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import useCanRespondToDecisions from "../../../../../hooks/useCanRespondToDecisions";
import {
  acceptFutureDealOption,
  clearRuleEngineResponse,
  fetchDealUploadStatus,
  fetchRuleEngineDecisionReasoning,
  rejectFutureDealOption,
  respondToRuleEngineDecision,
  storeAnalyticsSelector,
  updateDealUploadStatus,
  upsertProductComment,
} from "../../../../../redux/slices/store_analytics";
import { trackEvent } from "../../../../../utils/analytics";
import { AGENT_INFO, AWD_SELLER_CENTRAL_URL } from "../../constants";

import DaveDealAction from "./DaveDealAction";
import IrisDecisionTreeModal from "./IrisDecisionTreeModal/IrisDecisionTreeModal";
import TriggeredByAgent from "./TriggeredByAgent";

// "VERY LOW INVENTORY WARNING" -> "Very Low Inventory"
const formatReasonLabel = (text) => {
  if (!text) return "";
  return text
    .replace(/\s*WARNING$/i, "")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Helper function to parse and format reasoning text
const formatReasoningText = (text) => {
  if (!text) return [];
  const parts = text.split(/(\s*-\s+)/);
  const formattedParts = [];

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];

    if (/^\s*-\s+$/.test(part)) {
      continue;
    }

    if (i > 0 && /^\s*-\s+$/.test(parts[i - 1])) {
      formattedParts.push({
        type: "list-item",
        content: part.trim(),
      });
    } else if (part.trim()) {
      formattedParts.push({
        type: "text",
        content: part.trim(),
      });
    }
  }

  return formattedParts;
};

const RuleEngineDecision = ({
  asin,
  sku,
  executionId,
  targetPrice,
  sellingPrice,
  action,
  currentPrice,
  store,
  marketplace,
  decisionType,
  dealAvailable,
  deal,
  onRemove,
  agentName: agentNameProp,
  automatedExecutionStatus,
  onDealRejected,
  // Optional overrides for cards another agent triggers but Iris executes
  // (Ross price reverts), all unset for Iris's own decisions:
  // - triggeredBy: { agent, reason } shown as a "Triggered By" row
  // - failedMessage: copy for the all_attempts_failed box
  // - reasonLines: inline "Why?" content, for cards with no Iris decision tree
  triggeredBy,
  failedMessage,
  reasonLines,
}) => {
  const dispatch = useDispatch();
  const {
    saveRuleEngineResponseLoading: saveRuleEngineResponseLoadingMap,
    saveRuleEngineResponseStatus,
    ruleEngineDecisionReasoningData,
    ruleEngineDecisionReasoningLoading,
    dealUploadStatus: dealUploadStatusMap,
  } = useSelector(storeAnalyticsSelector);
  const agentName = (
    agentNameProp || (action.includes("Profit -") ? "Pete" : "Iris")
  ).toLowerCase();
  const statusKey = `${executionId}_${asin}_${agentName}`;
  const saveRuleEngineResponseLoading =
    !!saveRuleEngineResponseLoadingMap[statusKey];
  const inputRef = useRef(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showAcceptInput, setShowAcceptInput] = useState(false);
  const [acceptReason, setAcceptReason] = useState("");
  const [showReasoning, setShowReasoning] = useState(false);
  const [showDecisionTree, setShowDecisionTree] = useState(false);
  const [showDealConfirmation, setShowDealConfirmation] = useState(false);
  const [dealRejected, setDealRejected] = useState(false); // Track if user rejected the deal to show original flow
  const [dealRejectionLoading, setDealRejectionLoading] = useState(false); // Loading state for deal rejection
  const [showDealRejectReasonInput, setShowDealRejectReasonInput] =
    useState(false);
  const [dealRejectReason, setDealRejectReason] = useState("");
  const pollingIntervalRef = useRef(null);

  // Get deal upload status from Redux (persisted across component remounts)
  const dealUploadStatusData = dealUploadStatusMap?.[asin] || {};
  const dealUploadStatus = dealUploadStatusData.status || null;
  const dealAccepted = dealUploadStatusData.dealAccepted || false;
  const ruleEngineResponseSent =
    dealUploadStatusData.ruleEngineResponseSent || false;
  const sellerCentralUrl = dealUploadStatusData.sellerCentralUrl || null;

  // Check if this is an "Increase Velocity" action with a deal available
  const isIncreaseVelocity = action.includes("Increase Velocity");
  // Show deal option only if deal is available AND user hasn't rejected or accepted it yet
  const shouldShowDealOption =
    isIncreaseVelocity &&
    dealAvailable &&
    deal &&
    !dealRejected &&
    !dealAccepted;
  // Dave is the one executing the deal (proposed or already accepted) — the
  // card art should reflect whoever is acting, not just whoever sensed it.
  // Reverts to the sensing agent once the deal is explicitly rejected.
  const showDaveArt =
    isIncreaseVelocity && dealAvailable && !!deal && !dealRejected;

  const userAllowedToRespond = useCanRespondToDecisions(store);

  // for adding the comment documentation
  const today = new Date();
  const start_date = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0];
  const end_date = today.toISOString().split("T")[0];
  const cellForCommentDocumentation = today.toISOString().slice(5, 10);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Resume polling if there's an active upload in progress when component mounts
  useEffect(() => {
    const batchId = dealUploadStatusData.batchId;
    const status = dealUploadStatusData.status;

    // If we have a batchId and status is not terminal, resume polling
    if (
      batchId &&
      status &&
      status !== "confirmed" &&
      status !== "completed" &&
      status !== "failed" &&
      !pollingIntervalRef.current
    ) {
      // Poll immediately on mount
      pollUploadStatus(batchId);
      // Then poll every 5 seconds
      pollingIntervalRef.current = setInterval(() => {
        pollUploadStatus(batchId);
      }, 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus the input field when reject input is shown
  useEffect(() => {
    if (showRejectInput && inputRef.current) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    }
  }, [showRejectInput]);

  // Focus the input field when accept input is shown
  useEffect(() => {
    if (showAcceptInput && inputRef.current) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    }
  }, [showAcceptInput]);

  const acceptButtonClicked = () => {
    trackEvent("recommendation_accept_clicked", {
      asin,
      store,
      decision_type: decisionType,
    });
    if (decisionType === "price" || decisionType === "inventory") {
      setShowAcceptInput(false);
      setShowConfirmation(true);
    } else if (decisionType === "ppc") {
      setShowAcceptInput(true);
      setShowConfirmation(false);
    }
  };

  const confirmAccept = async () => {
    trackEvent("recommendation_accepted", {
      asin,
      store,
      decision_type: decisionType,
      execution_id: executionId,
    });
    const payload = {
      asin: asin,
      sku: sku,
      store: store,
      execution_id: executionId,
      status_key: statusKey,
      marketplace: marketplace,
      user_response: "Accept",
    };
    if (decisionType === "ppc") {
      payload.accept_reason = acceptReason;
    }
    if (decisionType === "price") {
      payload.target_price = targetPrice;
      payload.selling_price = sellingPrice;
    }
    const result = await dispatch(respondToRuleEngineDecision(payload));
    if (result.success) {
      setShowConfirmation(false);
      setAcceptReason("");
      if (decisionType === "ppc") {
        window.open(
          "https://advertising.amazon.com/campaign-manager",
          "_blank"
        );
      }
      if (decisionType === "inventory") {
        // Rita: the replenishment order itself is created in Seller Central
        window.open(AWD_SELLER_CENTRAL_URL, "_blank");
      }

      const commentObj = {
        asin: asin,
        store: store,
        cell: cellForCommentDocumentation,
        text:
          "Accepted " +
          decisionType +
          " recommendation: " +
          (decisionType === "ppc"
            ? acceptReason
            : decisionType === "inventory"
            ? action // e.g. Rita's "Replenish Inventory - Move Units from AWD to FBA": no price to quote
            : "$" + sellingPrice + " -> $" + targetPrice),
        user: "Agent " + agentName + " ",
      };
      await dispatch(
        upsertProductComment(
          commentObj,
          store,
          start_date,
          end_date,
          marketplace
        )
      );
    }
  };

  const confirmReject = async () => {
    trackEvent("recommendation_rejected", {
      asin,
      store,
      decision_type: decisionType,
      execution_id: executionId,
    });
    const payload = {
      asin: asin,
      sku: sku,
      store: store,
      execution_id: executionId,
      status_key: statusKey,
      marketplace: marketplace,
      user_response: "Reject",
      reject_reason: rejectReason,
    };
    const result = await dispatch(respondToRuleEngineDecision(payload));
    if (result.success) {
      setShowRejectInput(false);
      setRejectReason("");

      const commentObj = {
        asin: asin,
        store: store,
        cell: cellForCommentDocumentation,
        text:
          "Rejected " +
          decisionType +
          " recommendation: " +
          (decisionType === "ppc"
            ? rejectReason
            : decisionType === "inventory"
            ? action + " because '" + rejectReason + "'"
            : "$" +
              sellingPrice +
              " -> $" +
              targetPrice +
              " because '" +
              rejectReason +
              "'"),
        user: "Agent " + agentName + " ",
      };
      await dispatch(
        upsertProductComment(
          commentObj,
          store,
          start_date,
          end_date,
          marketplace
        )
      );
    }
  };

  const pollUploadStatus = async (batchId) => {
    const result = await dispatch(fetchDealUploadStatus(batchId, store));
    if (result.success) {
      const status = result.data.status;
      const sellerCentralUrl = result.data.seller_central_url;
      dispatch(
        updateDealUploadStatus({
          asin,
          status,
          batchId,
          dealAccepted: true,
          sellerCentralUrl,
        })
      );

      // Stop polling if we've reached a terminal state
      if (
        status === "confirmed" ||
        status === "failed" ||
        status === "completed"
      ) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        // Only record rule engine response after successful upload confirmation
        if (
          (status === "confirmed" || status === "completed") &&
          !ruleEngineResponseSent
        ) {
          dispatch(
            updateDealUploadStatus({
              asin,
              status,
              batchId,
              dealAccepted: true,
              ruleEngineResponseSent: true,
            })
          );

          // Mark the rule engine decision as accepted in rule_engine_responses table
          const ruleEnginePayload = {
            asin: asin,
            sku: sku,
            execution_id: executionId,
            status_key: statusKey,
            user_response: "Accept",
            target_price: targetPrice,
            selling_price: sellingPrice,
            accept_reason: `Scheduled Best Deal: ${deal.discount_percent}% off ($${deal.deal_price})`,
            decision_type: decisionType,
            store: store,
            marketplace: marketplace,
          };
          dispatch(respondToRuleEngineDecision(ruleEnginePayload));
        }
      }
    }
  };

  const confirmDealAccept = async () => {
    trackEvent("deal_accepted", { asin, store, deal_id: deal?.id });
    const payload = {
      asin: asin,
      dealId: deal.id,
      store,
      execution_id: executionId,
      status_key: statusKey,
      notes: `Accepted via Iris recommendation for ASIN ${asin}`,
    };
    const result = await dispatch(acceptFutureDealOption(payload));
    if (result.success) {
      setShowDealConfirmation(false);

      // Start polling for upload status if batch_id is returned
      if (result.data?.batch_id && result.data?.upload_started) {
        dispatch(
          updateDealUploadStatus({
            asin,
            status: "pending",
            batchId: result.data.batch_id,
            dealAccepted: true,
          })
        );
        // Poll every 5 seconds
        pollingIntervalRef.current = setInterval(() => {
          pollUploadStatus(result.data.batch_id);
        }, 5000);
        // Also poll immediately
        pollUploadStatus(result.data.batch_id);
      }

      // Note: Rule engine response is now recorded in pollUploadStatus
      // only after upload is confirmed, to ensure data consistency

      const commentObj = {
        asin: asin,
        store: store,
        cell: cellForCommentDocumentation,
        text: (() => {
          const pricePart =
            deal.discount_percent != null && deal.deal_price != null
              ? `Scheduled Best Deal: ${deal.discount_percent}% off ($${deal.deal_price})`
              : "Scheduled Best Deal";
          const startDate = deal.adjusted_start_date || deal.start_date;
          const endDate = deal.adjusted_end_date || deal.end_date;
          const datePart =
            startDate && endDate ? ` from ${startDate} to ${endDate}` : "";
          return `Handled by ${AGENT_INFO.dave.name} — ${pricePart}${datePart}`;
        })(),
        user: "Agent " + agentName + " ",
      };
      dispatch(upsertProductComment(commentObj, store, start_date, end_date));
    }
  };

  const handleDealReject = async () => {
    trackEvent("deal_rejected", { asin, store, deal_id: deal?.id });
    setDealRejectionLoading(true);
    const payload = {
      dealId: deal.id,
      store,
      notes: dealRejectReason,
    };
    const result = await dispatch(rejectFutureDealOption(payload));
    setDealRejectionLoading(false);
    if (result.success) {
      setDealRejected(true);
      setShowDealConfirmation(false);
      setShowDealRejectReasonInput(false);
      onDealRejected?.();

      // Document the rejection
      const commentObj = {
        asin: asin,
        store: store,
        cell: cellForCommentDocumentation,
        text: `Handled by ${AGENT_INFO.dave.name} — Rejected Best Deal (${deal.discount_percent}% off): ${dealRejectReason}`,
        user: "Agent " + agentName + " ",
      };
      dispatch(upsertProductComment(commentObj, store, start_date, end_date));
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        position: "relative",
        marginLeft: "-15px",
        marginRight: "-15px",
        paddingLeft: "calc(16px + 15px)",
        paddingRight: "calc(16px + 15px)",
        minHeight: "120px", // Prevent shrinking during processing
        minWidth: "400px", // Prevent width changes during processing
        width: "100%", // Maintain full available width
      }}
    >
      <img
        src={showDaveArt ? AGENT_INFO.dave.avatar : `/assets/${agentName}.png`}
        alt={showDaveArt ? AGENT_INFO.dave.name : agentName}
        style={{
          position: "absolute",
          bottom: -20,
          right: -31,
          width: 100,
          height: 100,
          opacity: 0.7,
        }}
      />

      {saveRuleEngineResponseLoading || ruleEngineDecisionReasoningLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
          <div
            style={{
              width: 16,
              height: 16,
              border: "2px solid #e3f2fd",
              borderTop: "2px solid #1976d2",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <Typography variant="body2" sx={{ color: "#1976d2" }}>
            Processing...
          </Typography>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </Box>
      ) : !saveRuleEngineResponseStatus[statusKey]?.message ? (
        <>
          {!dealAccepted &&
            (() => {
              // Parse action to separate warning prefix from the actual action
              // e.g., "VERY LOW INVENTORY WARNING: Reduce Velocity - Increase Price"
              // becomes warning: "VERY LOW INVENTORY WARNING", actionPart: "Reduce Velocity - Increase Price"
              const warningMatch = action?.match(/^(.+?WARNING):\s*(.+)$/i);
              const warningText = warningMatch ? warningMatch[1] : null;
              const actionPart = warningMatch ? warningMatch[2] : action;
              const actionParts = actionPart?.split("-") || [];
              const intent = actionParts[0]?.trim(); // e.g., "Reduce Velocity"
              const method = actionParts[1]?.trim(); // e.g., "Increase Price"
              const sensingAgent = AGENT_INFO[agentName] || AGENT_INFO.iris;
              const triggerReason = warningText
                ? formatReasonLabel(warningText)
                : intent;

              return (
                <>
                  {triggeredBy ? (
                    <TriggeredByAgent
                      agent={triggeredBy.agent}
                      reason={triggeredBy.reason}
                    />
                  ) : (
                    shouldShowDealOption && (
                      <TriggeredByAgent
                        agent={sensingAgent}
                        reason={triggerReason}
                      />
                    )
                  )}
                  {warningText && (
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        color: "#d32f2f",
                        fontSize: "0.8rem",
                      }}
                    >
                      {warningText}
                    </Typography>
                  )}
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 400,
                      mb: 2,
                      color: "#666",
                      fontStyle: "italic",
                    }}
                  >
                    {dealRejected ? "Alternative Option - " : "I Suggest to "}
                    {method ? (
                      <>
                        {intent + " "}
                        {shouldShowDealOption ? (
                          <>
                            {"Using"}
                            <br />
                            <span
                              style={{
                                fontSize: "0.9125rem",
                                fontWeight: "bold",
                                fontStyle: "normal",
                                color: "#1976d2",
                                marginLeft: "0.25rem",
                              }}
                            >
                              Schedule a Best Deal:
                            </span>
                          </>
                        ) : (
                          <>
                            {"Using"}
                            <br />
                            <span
                              style={{
                                fontSize: "0.9125rem",
                                fontWeight: "bold",
                                fontStyle: "normal",
                                color: "black",
                                marginLeft: "0.25rem",
                              }}
                            >
                              {method + ":"}
                            </span>
                          </>
                        )}
                      </>
                    ) : (
                      actionPart
                    )}
                  </Typography>
                </>
              );
            })()}

          {/* Show Deal Option when available for Increase Velocity */}
          {shouldShowDealOption ? (
            <DaveDealAction
              deal={deal}
              currentPrice={currentPrice || deal.seller_price}
              acceptDisabled={
                !userAllowedToRespond ||
                saveRuleEngineResponseLoading ||
                showDealConfirmation ||
                dealRejectionLoading
              }
              rejectDisabled={
                !userAllowedToRespond ||
                saveRuleEngineResponseLoading ||
                showDealConfirmation ||
                dealRejectionLoading
              }
              confirmDisabled={saveRuleEngineResponseLoading}
              rejectLoading={dealRejectionLoading}
              showDealConfirmation={showDealConfirmation}
              onAcceptClick={() => setShowDealConfirmation(true)}
              onConfirmAccept={confirmDealAccept}
              onCancelConfirmation={() => setShowDealConfirmation(false)}
              onReject={() => setShowDealRejectReasonInput(true)}
              showRejectReasonInput={showDealRejectReasonInput}
              rejectReason={dealRejectReason}
              onRejectReasonChange={setDealRejectReason}
              onConfirmReject={handleDealReject}
              onCancelReject={() => {
                setShowDealRejectReasonInput(false);
                setDealRejectReason("");
              }}
            />
          ) : dealAccepted ? (
            <>
              {/* Deal Accepted - Show Upload Status */}
              <Typography
                variant="body2"
                sx={{ color: "#2e7d32", fontWeight: 500, mb: 2 }}
              >
                {dealUploadStatus === "confirmed" ||
                dealUploadStatus === "completed"
                  ? "Best Deal Scheduled Successfully"
                  : "Scheduling Best Deal..."}
              </Typography>
              {dealUploadStatus && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor:
                      dealUploadStatus === "confirmed" ||
                      dealUploadStatus === "completed"
                        ? "#e8f5e9"
                        : dealUploadStatus === "failed"
                        ? "#ffebee"
                        : "#e3f2fd",
                    border: `1px solid ${
                      dealUploadStatus === "confirmed" ||
                      dealUploadStatus === "completed"
                        ? "#a5d6a7"
                        : dealUploadStatus === "failed"
                        ? "#ef9a9a"
                        : "#90caf9"
                    }`,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        dealUploadStatus === "confirmed" ||
                        dealUploadStatus === "completed"
                          ? "#2e7d32"
                          : dealUploadStatus === "failed"
                          ? "#c62828"
                          : "#1565c0",
                      fontWeight: 500,
                    }}
                  >
                    {dealUploadStatus === "pending" &&
                      "Starting deal upload process..."}
                    {dealUploadStatus === "generating" &&
                      "Generating deal file... Please wait."}
                    {dealUploadStatus === "uploading" &&
                      "Uploading to Seller Central... This may take a few minutes."}
                    {dealUploadStatus === "uploaded" &&
                      "Upload complete. Waiting for confirmation..."}
                    {(dealUploadStatus === "confirmed" ||
                      dealUploadStatus === "completed") &&
                      "Deal successfully uploaded! Check Seller Central to verify."}
                    {dealUploadStatus === "failed" &&
                      "Upload failed. Please contact support for assistance."}
                  </Typography>
                  {(dealUploadStatus === "pending" ||
                    dealUploadStatus === "generating" ||
                    dealUploadStatus === "uploading" ||
                    dealUploadStatus === "uploaded") && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mt: 1,
                        gap: 1,
                      }}
                    >
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          border: "2px solid #90caf9",
                          borderTop: "2px solid #1976d2",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      />
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        Processing...
                      </Typography>
                      <style>
                        {`
                          @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                          }
                        `}
                      </style>
                    </Box>
                  )}
                  {(dealUploadStatus === "confirmed" ||
                    dealUploadStatus === "completed") &&
                    sellerCentralUrl && (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          mt: 2,
                          backgroundColor: "#ff9800",
                          textTransform: "none",
                          fontWeight: 500,
                          boxShadow: "none",
                          borderRadius: 3,
                          "&:hover": {
                            backgroundColor: "#f57c00",
                            boxShadow: "none",
                          },
                        }}
                        onClick={() => {
                          trackEvent("seller_central_opened", { asin, store });
                          window.open(sellerCentralUrl, "_blank");
                        }}
                      >
                        Open in Seller Central
                      </Button>
                    )}
                </Box>
              )}
            </>
          ) : (
            <>
              {/* Original Price/PPC recommendation UI */}
              {targetPrice && (
                <Box sx={{ mb: 3 }}>
                  <Tooltip
                    title={
                      currentPrice
                        ? `The price when the offer was generated was $${sellingPrice}, but it has been detected that the current price is $${currentPrice}`
                        : ""
                    }
                    disableHoverListener={
                      !currentPrice || sellingPrice === currentPrice
                    }
                    arrow
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        mb: 0.5,
                        fontStyle: "italic",
                        cursor:
                          currentPrice && currentPrice !== sellingPrice
                            ? "help"
                            : "default",
                      }}
                    >
                      Current:{" "}
                      {currentPrice && currentPrice !== sellingPrice ? (
                        <>
                          <span style={{ textDecoration: "line-through" }}>
                            ${sellingPrice}
                          </span>{" "}
                          ${currentPrice}
                        </>
                      ) : (
                        `$${sellingPrice}`
                      )}
                    </Typography>
                  </Tooltip>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666", fontStyle: "italic" }}
                  >
                    Target:{" "}
                    <strong
                      style={{
                        fontWeight: 700,
                        fontSize: "0.9125rem",
                        color: "black",
                        fontStyle: "normal",
                        marginLeft: "0.25rem",
                      }}
                    >
                      ${targetPrice}
                    </strong>
                  </Typography>
                </Box>
              )}

              {/* TODO: in_progress cards are disabled by now, if not used in a future just delete it */}
              {/* {agentName === "iris" &&
                automatedExecutionStatus === "in_progress" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        border: "2px solid #e3f2fd",
                        borderTop: "2px solid #1976d2",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: "#1976d2", fontWeight: 500 }}
                    >
                      Automating…
                    </Typography>
                    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                  </Box>
                )} */}

              {agentName === "iris" &&
                automatedExecutionStatus === "succeeded" && (
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1.5,
                        backgroundColor: "#e8f5e9",
                        borderRadius: 2,
                        border: "1px solid #a5d6a7",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "#2e7d32", fontWeight: 500 }}
                      >
                        Price Updated Automatically ✓
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "#aaa", fontStyle: "italic" }}
                    >
                      If you'd like to change this back, you can do so through
                      Seller Central.
                    </Typography>
                  </Box>
                )}

              {agentName === "iris" &&
                automatedExecutionStatus === "all_attempts_failed" && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      backgroundColor: "#ffebee",
                      borderRadius: 2,
                      border: "1px solid #ef9a9a",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#c62828", fontWeight: 500 }}
                    >
                      {failedMessage ||
                        "We tried to increase Iris's price automatically, but it failed. Please review the suggestion and accept or reject it to ensure price changes."}
                    </Typography>
                  </Box>
                )}

              {(agentName !== "iris" ||
                !automatedExecutionStatus ||
                automatedExecutionStatus === "all_attempts_failed") && (
                <Box
                  sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    disabled={
                      !userAllowedToRespond ||
                      saveRuleEngineResponseLoading ||
                      showConfirmation ||
                      showRejectInput ||
                      showAcceptInput
                    }
                    sx={{
                      backgroundColor: userAllowedToRespond
                        ? "#4caf50"
                        : "#e0e0e0",
                      color: userAllowedToRespond ? "#fff" : "#9e9e9e",
                      textTransform: "none",
                      fontWeight: 500,
                      boxShadow: "none",
                      borderRadius: 3,
                      "&:hover": userAllowedToRespond
                        ? {
                            backgroundColor: "#45a049",
                            boxShadow: "none",
                          }
                        : {},
                    }}
                    onClick={
                      userAllowedToRespond ? acceptButtonClicked : undefined
                    }
                  >
                    Accept
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    disabled={
                      !userAllowedToRespond ||
                      saveRuleEngineResponseLoading ||
                      showConfirmation ||
                      showRejectInput ||
                      showAcceptInput
                    }
                    sx={{
                      color: userAllowedToRespond ? "#f44336" : "#9e9e9e",
                      borderColor: userAllowedToRespond ? "#f44336" : "#e0e0e0",
                      textTransform: "none",
                      fontWeight: 500,
                      borderRadius: 3,
                      "&:hover": userAllowedToRespond
                        ? {
                            borderColor: "#d32f2f",
                            backgroundColor: "transparent",
                          }
                        : {},
                    }}
                    onClick={
                      userAllowedToRespond
                        ? () => {
                            setShowRejectInput(true);
                            setShowConfirmation(false);
                            setShowAcceptInput(false);
                          }
                        : undefined
                    }
                  >
                    Reject
                  </Button>

                  <Button
                    variant={
                      agentName === "iris"
                        ? "outlined"
                        : showReasoning
                        ? "contained"
                        : "outlined"
                    }
                    size="small"
                    onClick={() => {
                      trackEvent("recommendation_reasoning_toggle", {
                        asin,
                        store,
                        show: true,
                      });
                      if (reasonLines) {
                        setShowReasoning((v) => !v);
                      } else if (agentName === "iris") {
                        setShowDecisionTree(true);
                      } else if (!showReasoning) {
                        setShowReasoning(true);
                        dispatch(
                          fetchRuleEngineDecisionReasoning({
                            store,
                            marketplace,
                            asin,
                            sku,
                            executionId,
                          })
                        );
                      } else {
                        setShowReasoning(false);
                      }
                    }}
                    sx={{
                      ...(agentName !== "iris" && showReasoning
                        ? {
                            backgroundColor: "#64b5f6",
                            color: "#fff",
                            "&:hover": {
                              backgroundColor: "#42a5f5",
                            },
                          }
                        : {
                            color: "#64b5f6",
                            borderColor: "#64b5f6",
                            "&:hover": {
                              borderColor: "#42a5f5",
                              backgroundColor: "transparent",
                            },
                          }),
                      textTransform: "none",
                      fontWeight: 500,
                      borderRadius: 20,
                    }}
                  >
                    Why?
                  </Button>
                </Box>
              )}
            </>
          )}

          {showReasoning && reasonLines && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#f8f9fa",
                borderRadius: 2,
                border: "1px solid #e9ecef",
              }}
            >
              {reasonLines.map((line) => (
                <Typography
                  key={line}
                  variant="body2"
                  sx={{ color: "#495057" }}
                >
                  {line}
                </Typography>
              ))}
            </Box>
          )}

          {showReasoning && !reasonLines && agentName !== "iris" && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#f8f9fa",
                borderRadius: 2,
                border: "1px solid #e9ecef",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#495057",
                  fontWeight: 600,
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                Lets look at the reasoning:
              </Typography>

              {ruleEngineDecisionReasoningData[asin]?.message && (
                <Box sx={{ color: "#495057", lineHeight: 1.6 }}>
                  {formatReasoningText(
                    ruleEngineDecisionReasoningData[asin].message
                  ).map((part, index) => (
                    <Box
                      key={index}
                      sx={{ mb: part.type === "list-item" ? 1 : 1.5 }}
                    >
                      {part.type === "list-item" ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#1976d2",
                              fontWeight: 600,
                              minWidth: "20px",
                              mt: 0.1,
                            }}
                          >
                            •
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#495057", flex: 1 }}
                          >
                            {part.content}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: "#495057" }}>
                          {part.content}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
          {!userAllowedToRespond && (
            <Typography
              variant="body2"
              sx={{
                color: "red",
                fontStyle: "italic",
                mb: 2,
                textAlign: "center",
              }}
            >
              Only the store owner can accept or reject recommendations.
            </Typography>
          )}

          {showAcceptInput && (
            <>
              <Divider sx={{ my: 2 }} />
              <TextField
                inputRef={inputRef}
                fullWidth
                size="small"
                value={acceptReason}
                disabled={
                  saveRuleEngineResponseLoading ||
                  ruleEngineDecisionReasoningLoading
                }
                onChange={(e) => setAcceptReason(e.target.value)}
                placeholder="Enter accept reason"
                variant="outlined"
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#ffffff",
                  },
                }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={
                    saveRuleEngineResponseLoading ||
                    ruleEngineDecisionReasoningLoading ||
                    !acceptReason
                  }
                  sx={{
                    backgroundColor: "#4caf50",
                    textTransform: "none",
                    fontWeight: 500,
                    boxShadow: "none",
                    borderRadius: 3,
                    "&:hover": {
                      backgroundColor: "#45a049",
                      boxShadow: "none",
                    },
                  }}
                  onClick={confirmAccept}
                >
                  Submit
                </Button>
                <Button
                  variant="text"
                  size="small"
                  disabled={
                    saveRuleEngineResponseLoading ||
                    ruleEngineDecisionReasoningLoading
                  }
                  sx={{
                    color: "#666",
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: 3,
                  }}
                  onClick={() => {
                    setShowAcceptInput(false);
                    setAcceptReason("");
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </>
          )}

          {showRejectInput && (
            <>
              <Divider sx={{ my: 2 }} />
              <TextField
                inputRef={inputRef}
                fullWidth
                size="small"
                value={rejectReason}
                disabled={
                  saveRuleEngineResponseLoading ||
                  ruleEngineDecisionReasoningLoading
                }
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reject reason"
                variant="outlined"
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#ffffff",
                  },
                }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={
                    saveRuleEngineResponseLoading ||
                    ruleEngineDecisionReasoningLoading ||
                    !rejectReason
                  }
                  sx={{
                    backgroundColor: "#f44336",
                    textTransform: "none",
                    fontWeight: 500,
                    boxShadow: "none",
                    borderRadius: 3,
                    "&:hover": {
                      backgroundColor: "#d32f2f",
                      boxShadow: "none",
                    },
                  }}
                  onClick={confirmReject}
                >
                  Submit
                </Button>
                <Button
                  variant="text"
                  size="small"
                  disabled={
                    saveRuleEngineResponseLoading ||
                    ruleEngineDecisionReasoningLoading
                  }
                  sx={{
                    color: "#666",
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: 3,
                  }}
                  onClick={() => {
                    setShowRejectInput(false);
                    setRejectReason("");
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </>
          )}

          {showConfirmation && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
                {decisionType === "inventory"
                  ? "Record the acceptance and open FBA Inventory in Seller Central to move units from AWD?"
                  : "Confirm Price Updating?"}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={
                    saveRuleEngineResponseLoading ||
                    ruleEngineDecisionReasoningLoading
                  }
                  sx={{
                    backgroundColor: "#4caf50",
                    textTransform: "none",
                    fontWeight: 500,
                    boxShadow: "none",
                    borderRadius: 3,
                    "&:hover": {
                      backgroundColor: "#45a049",
                      boxShadow: "none",
                    },
                  }}
                  onClick={confirmAccept}
                >
                  Yes
                </Button>
                <Button
                  variant="text"
                  size="small"
                  disabled={
                    saveRuleEngineResponseLoading ||
                    ruleEngineDecisionReasoningLoading
                  }
                  sx={{
                    color: "#666",
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: 3,
                  }}
                  onClick={() => {
                    setShowConfirmation(false);
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </>
          )}
        </>
      ) : (
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: saveRuleEngineResponseStatus[statusKey]?.success
                ? "#4caf50"
                : "#f44336",
              fontWeight: 500,
              py: 1,
            }}
          >
            {saveRuleEngineResponseStatus[statusKey]?.message}
          </Typography>
          {onRemove && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                trackEvent("recommendation_dismissed", {
                  asin,
                  store,
                  execution_id: executionId,
                });
                dispatch(
                  clearRuleEngineResponse({
                    execution_id: executionId,
                    status_key: statusKey,
                  })
                );
                onRemove();
              }}
              sx={{
                mt: 1,
                color: "#666",
                borderColor: "#e0e0e0",
                textTransform: "none",
                fontWeight: 500,
                borderRadius: 3,
                "&:hover": {
                  borderColor: "#999",
                  backgroundColor: "transparent",
                },
              }}
            >
              Close
            </Button>
          )}
        </Box>
      )}

      {agentName === "iris" && (
        <IrisDecisionTreeModal
          open={showDecisionTree}
          onClose={() => setShowDecisionTree(false)}
          asin={asin}
          executionId={executionId}
          store={store}
          marketplace={marketplace}
        />
      )}
    </Box>
  );
};

export default RuleEngineDecision;
